import {
  changeAccountPasswordRequest,
  getAccessToken,
  getBuyerMinimumPasswordLength,
  getChangeAccountPassword,
  resetChangeAccountPassword,
} from '@app/ducks';
import { paths } from '@app/paths';
import { useValidatedForm } from '@app/utils/hooks/useValidatedForm';
import { isDefined } from '@app/utils/is';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { Form, FormFieldset, FormGrid, InputField } from '../various/Form';
import { PageTitle } from '../various/PageTitle';

import { AccountLayout } from './AccountLayout';

const defaultValues = {
  currentPassword: '',
  newPassword: '',
  reNewPassword: '',
};

type FormData = typeof defaultValues;

export const AccountPassword = () => {
  const { t } = useTranslation(['accounts', 'common', 'validation', 'passwordManagement']);
  const dispatch = useDispatch();
  const userToken = useSelector(getAccessToken);
  const minimumPasswordLength = useSelector(getBuyerMinimumPasswordLength);
  const { isLoading, isFailed, data: response } = useSelector(getChangeAccountPassword);

  const validationSchema: yup.ObjectSchema<FormData> = yup.object({
    currentPassword: yup.string().required(t('validation:password_hint')),
    newPassword: yup
      .string()
      .required(t('validation:new_password_hint'))
      .min(minimumPasswordLength, t('validation:password_length_hint', { count: minimumPasswordLength })),
    reNewPassword: yup
      .string()
      .required(t('validation:repeat_password_hint'))
      .oneOf([yup.ref('newPassword')], t('validation:password_match_hint')),
  });

  const formMethods = useValidatedForm<FormData>({
    defaultValues,
    validationSchema,
  });

  const { register, reset, setError } = formMethods;

  React.useEffect(() => {
    if (isFailed && isDefined(response.msg)) {
      setError('currentPassword', { message: response.msg }, { shouldFocus: true });
    }
  }, [isFailed, response.msg, setError]);

  React.useEffect(() => {
    if (!response.hasChanged) {
      return;
    }

    dispatch(resetChangeAccountPassword());
    reset();
  }, [dispatch, reset, response]);

  const handleCancel = () => {
    reset();
  };

  const handleSubmit = ({ currentPassword, newPassword }: FormData) => {
    if (!isDefined(userToken)) {
      return;
    }

    dispatch(
      changeAccountPasswordRequest({
        currentPassword,
        newPassword,
        token: userToken,
      }),
    );
  };

  const formName = 'changePassword';

  return (
    <PageTitle title={t('accounts:my_account')}>
      <AccountLayout
        activePath={paths.ACCOUNT_PASSWORD}
        isLoading={false}
        hasChanges={formMethods.formState.isDirty}
        onCancel={handleCancel}
        formName={formName}
      >
        <section className="box flex flex-col gap-5">
          <h3 className="heading-3">{t('passwordManagement:change_password')}</h3>
          <p className="text-neutral-40 body-2">{t('passwordManagement:change_password_subtitle')}</p>
          <Form id={formName} formMethods={formMethods} onSubmit={handleSubmit}>
            <FormFieldset isDisabled={isLoading}>
              <FormGrid colGap="medium">
                <InputField type="password" label={t('passwordManagement:current_password')} isRequired {...register('currentPassword')} />
                <InputField type="password" label={t('passwordManagement:new_password')} isRequired {...register('newPassword')} />
                <InputField type="password" label={t('passwordManagement:repeat_password')} isRequired {...register('reNewPassword')} />
              </FormGrid>
            </FormFieldset>
          </Form>
        </section>
      </AccountLayout>
    </PageTitle>
  );
};
